import './ChangeUserPlanModal.scss';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { S_Checkbox } from '../../../../design/inputs/checkbox/S_Checkbox';
import { Select } from '../../../../design/inputs/select/Select';
import { S_FieldLabel } from '../../../../design/labels/fieldLabel/S_FIeldLabel';
import { S_Modal } from '../../../../design/modals/modal/S_Modal';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { RowLayout } from '../../../../modules/common/layouts/rowLayout/RowLayout';
import { handleCheck } from '../../../helpers/formHelpers';
import { InputStatus } from '../../../models/error.model';
import { IPlanInfo, Plan } from '../../user/user.store';
import { NumericInputWithToggle } from './NumericInputWithToggle';

interface IChangeUserPlanModalProps {
  changeUserPlanModalShown: boolean;
  planInfo: IPlanInfo;
  planDefaults: IPlanInfo[];
  availableSpaceStatus: InputStatus;
  workspacesStatus: InputStatus;
  workspaceUsersStatus: InputStatus;
  onNumericInputBlur: (name: string) => void;
  onPlanChange: (plan: Plan) => void;
  onCustomLimitsChange: (value: boolean) => void;
  onLimitToggle: (name: string, unlimited: boolean) => void;
  onLimitChange: (name: string, value?: number) => void;
  onReset: () => void;
  onClose: () => void;
  onSavePlanClicked: () => Promise<void>;
}

export const ChangeUserPlanModal = observer((props: IChangeUserPlanModalProps): ReactElement => {
  const { t } = useTranslator();

  const handleUnlimitedToggle =
    (name: string) =>
    (unlimited: boolean): void => {
      props.onLimitToggle(name, unlimited);
    };
  const handleLimitChange =
    (name: string) =>
    (value?: number): void => {
      props.onLimitChange(name, value);
    };
  const handleReset = (): void => {
    props.onReset();
  };

  const planDefaults = props.planDefaults.find((p) => p.plan === (props.planInfo?.plan || Plan.Free));

  if (!planDefaults) {
    throw 'Plan not found';
  }

  const getOnBlur = (name: string) => (): void => {
    props.onNumericInputBlur(name);
  };

  const handlePlanChange = (value: Plan | null): void => {
    props.onPlanChange(value ?? Plan.Free);
  };

  const options = [
    { value: Plan.Free, label: t('Free') },
    { value: Plan.Annotation, label: t('Annotation') },
    { value: Plan.Training, label: t('Training') },
    { value: Plan.Inactive, label: t('Inactive') },
  ];

  return (
    <S_Modal
      isOpen={props.changeUserPlanModalShown}
      toggle={props.onClose}
      headerText={t('edit_plan_and_limits')}
      className='change-user-plan-modal'
      buttons={
        <RowLayout
          size='sm'
          spread
          className='plan-buttons'
        >
          <Button
            onClick={handleReset}
            disabled={!props.planInfo.customLimits}
          >
            {t('reset_to_defaults')}
          </Button>
          <RowLayout size='sm'>
            <Button
              id='change-button-cancel'
              color='neutral'
              variant='outlined'
              onClick={props.onClose}
            >
              {t('cancel')}
            </Button>
            <Button
              id='change-button'
              onClick={() => {
                void props.onSavePlanClicked();
              }}
            >
              {t('save')}
            </Button>
          </RowLayout>
        </RowLayout>
      }
    >
      <ColumnLayout size='md'>
        <div>
          <S_FieldLabel>{t('active_plan')}</S_FieldLabel>
          <Select
            id='user-change-plan-dropdown'
            value={options.find((x) => x.value === props.planInfo?.plan)}
            onChange={(_, v) => {
              handlePlanChange(v.value);
            }}
            options={options}
          />
        </div>
        <S_Checkbox
          id='user-change-plan-custom-limits-checkbox'
          checked={props.planInfo.customLimits}
          onChange={handleCheck(props.onCustomLimitsChange)}
          label={t('custom_limits')}
        />
        <NumericInputWithToggle
          name='availableSpace'
          checked={props.planInfo.availableSpace === -1}
          value={props.planInfo.availableSpace}
          default={planDefaults.availableSpace}
          disabled={!props.planInfo.customLimits}
          inputStatus={props.availableSpaceStatus}
          onToggle={handleUnlimitedToggle('availableSpace')}
          onValueChange={handleLimitChange('availableSpace')}
          onBlur={getOnBlur('availableSpace')}
        />
        <NumericInputWithToggle
          name='workspaces_label'
          checked={props.planInfo.workspaces === -1}
          value={props.planInfo.workspaces}
          default={planDefaults.workspaces}
          disabled={!props.planInfo.customLimits}
          inputStatus={props.workspacesStatus}
          onToggle={handleUnlimitedToggle('workspaces')}
          onValueChange={handleLimitChange('workspaces')}
          onBlur={getOnBlur('workspaces')}
        />
        <NumericInputWithToggle
          name='workspaceUsers'
          checked={props.planInfo.workspaceUsers === -1}
          value={props.planInfo.workspaceUsers}
          default={planDefaults.workspaceUsers}
          disabled={!props.planInfo.customLimits}
          inputStatus={props.workspaceUsersStatus}
          onToggle={handleUnlimitedToggle('workspaceUsers')}
          onValueChange={handleLimitChange('workspaceUsers')}
          onBlur={getOnBlur('workspaceUsers')}
        />
      </ColumnLayout>
    </S_Modal>
  );
});
